import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Container,
  Form,
  Grid,
  Icon,
  Popup,
  Segment,
  Select
} from 'semantic-ui-react'
import concat from 'lodash/concat'
import { format } from 'date-fns'
// Local
import { formatDob } from '../../common/formatUtils'
import {
  API_URL,
  ENCOUNTER,
  FORMAT,
  LIST_PAGE_SIZE,
  LIST_MAX_PAGES,
  LOCK,
  PATIENT,
  REVIEW,
  WORKLIST_VIEW,
  shouldRenderReskin2025
} from '../../../constant'
import { api } from '../../../action/api'
import { searchGenerator } from '../../../action/search'
import { updateUserWorklistFilters } from '../../../action/user'
import SubHeader from '../../common/SubHeader'
import Filter from '../../common/filter'
import Table from '../../common/Table'
import { patientMrn } from '../../common/patientUtils'
import { showVbcDisplay } from '../../common/currentUserUtils'
import CoderEncounterStatusIcon from './coderEncounterStatusIcon'
import { semiColonsToLineBreaks } from '../../v2/utils/utils'
import ReveleerControlButton from '../../common/ReveleerControlButton'
import FilterIcon from '../../icons/Filter'
import { User } from '../../authenticated/types'
import { RootState } from '../../../store'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>

type WorklistFilters = {
  encounterSort?: string
  view?: string
  start?: Date | null
  birthDate?: Date | null
  firstName?: string | null
  lastName?: string | null
  patientOrgId?: string | null
  appointmentType?: string | null
  practitionerId?: string | null
  patientRegion?: string | null
  vbcProgram?: string | null
  mrn?: string | null
  defaultPatientRegion?: string
}

type CurrentUser = User & {
  tenantId?: string
  worklistFilters: WorklistFilters
}

type Patient = {
  firstName: string
  lastName: string
  birthDate: string
  vbcProgram?: string
}

type Practitioner = {
  firstName: string
  lastName: string
}

type EncounterRecord = {
  id: string
  start: string
  appointmentType: string
  patient: Patient
  practitioner: Practitioner
}

type QueryFilters = {
  start?: Date | null
  birthDate?: Date | null
  firstName?: string | null
  lastName?: string | null
  patientOrgId?: string | null
  appointmentType?: string | null
  practitionerId?: string | null
  patientRegion?: string | null
  vbcProgram?: string | null
  mrn?: string | null
  reviewCode?: string[]
  encounterStatus?: string[]
  lockStatus?: string | null
  patientStatus?: string[]
  reviewQueryStatus?: string | null
}

type CoderWorklistOwnProps = {
  onSelect?: (
    id: string,
    event: React.MouseEvent
  ) => void
  userRole: string
}

type CoderWorklistReduxProps = {
  currentUser: CurrentUser
  updateUserWorklistFilters: (
    user: CurrentUser,
    filters: Partial<WorklistFilters>
  ) => void
}

type CoderWorklistProps = CoderWorklistOwnProps & CoderWorklistReduxProps

const worklistViewOptions = [
  {
    key: WORKLIST_VIEW.CODER.WORKLIST,
    text: 'Worklist',
    value: WORKLIST_VIEW.CODER.WORKLIST
  },
  {
    key: WORKLIST_VIEW.CODER.REVIEW_REQUEST,
    text: 'Coder Review Request',
    value: WORKLIST_VIEW.CODER.REVIEW_REQUEST
  },
  {
    key: WORKLIST_VIEW.CODER.READY_FOR_CODER_QA,
    text: 'Ready for Coder QA',
    value: WORKLIST_VIEW.CODER.READY_FOR_CODER_QA
  },
  {
    key: WORKLIST_VIEW.CODER.COMPLETED,
    text: 'Completed',
    value: WORKLIST_VIEW.CODER.COMPLETED
  },
  {
    key: WORKLIST_VIEW.CODER.READY_FOR_PHYSICIAN,
    text: 'Ready for Physician',
    value: WORKLIST_VIEW.CODER.READY_FOR_PHYSICIAN
  },
  {
    key: WORKLIST_VIEW.CODER.LOCKED,
    text: 'Locked',
    value: WORKLIST_VIEW.CODER.LOCKED
  }
]

/**
 * Provides a worklist view of encounters for the Coder user role
 *
 * Be aware that there is fussy logic around the encounter search date range.
 * If the user is viewing the Worklist encounter view, the encounters are
 * constrained to the recent past. These should be appointments,
 * ready-for-physician, that have recently finished -- unless the user has
 * explicitly picked a date.
 *
 * @param onSelect Callback function to execute when an encounter is selected
 *        arguments: Encounter ID
 */
function CoderWorklist(props: CoderWorklistProps): JSX.Element {
  const {
    onSelect,
    userRole,
    currentUser,
    updateUserWorklistFilters: updateFilters
  } = props

  const [
    encounters,
    setEncounters
  ] = useState<EncounterRecord[]>([])
  const [
    worklistView,
    setWorklistView
  ] = useState<string>(WORKLIST_VIEW.CODER.WORKLIST)
  const [
    pagesDisplayed,
    setPagesDisplayed
  ] = useState<number>(0)
  const [
    encounterSort,
    setEncounterSort
  ] = useState<string>('start asc')
  const [
    isFilterVisible,
    setIsFilterVisible
  ] = useState<boolean>(false)
  const [
    queryFilters,
    setQueryFilters
  ] = useState<QueryFilters>({})

  const fetchEncounters = (
    view: string,
    offset: number,
    query: QueryFilters,
    sort: string,
    currentEncounters: EncounterRecord[]
  ) => {
    const searchQuery: QueryFilters = query || {}
    const encounterStatus = [
      ENCOUNTER.STATUS.PLANNED,
      ENCOUNTER.STATUS.ARRIVED,
      ENCOUNTER.STATUS.FINISHED,
      ENCOUNTER.STATUS.IN_PROGRESS
    ]
    let reviewCode: string[] = [ REVIEW.CODE.READY_FOR_CODER ]
    const patientStatus = [ PATIENT.STATUS.ACTIVE ]
    let reviewQueryStatus: string | null = null
    let lockStatus: string | null = null

    switch (view) {
      case WORKLIST_VIEW.CODER.READY_FOR_CODER_QA:
        reviewCode = [ REVIEW.CODE.READY_FOR_CODER_QA ]
        break
      case WORKLIST_VIEW.CODER.REVIEW_REQUEST:
        reviewQueryStatus = REVIEW.QUERY_STATUS.REVIEW_QUERY_OUTSTANDING
        break
      case WORKLIST_VIEW.CODER.COMPLETED:
        reviewCode = [ REVIEW.CODE.COMPLETED ]
        break
      case WORKLIST_VIEW.CODER.READY_FOR_PHYSICIAN:
        reviewCode = [ REVIEW.CODE.READY_FOR_PHYSICIAN ]
        break
      case WORKLIST_VIEW.CODER.LOCKED:
        lockStatus = LOCK.STATUS.ACTIVE
        reviewCode = [
          REVIEW.CODE.READY_FOR_PHYSICIAN,
          REVIEW.CODE.COMPLETED
        ]
        break
      default:
        reviewCode = [ REVIEW.CODE.READY_FOR_CODER ]
    }

    if (!searchQuery.patientRegion) {
      const defaultRegions = currentUser.worklistFilters.defaultPatientRegion
      if (defaultRegions) {
        searchQuery.patientRegion = defaultRegions
      }
    }

    searchQuery.reviewCode = reviewCode
    searchQuery.encounterStatus = encounterStatus
    searchQuery.lockStatus = lockStatus
    searchQuery.patientStatus = patientStatus
    searchQuery.reviewQueryStatus = reviewQueryStatus

    const url = API_URL.ENCOUNTER_SEARCH_WITH_ROLE(userRole)
    const limit = offset === 0
      ? LIST_PAGE_SIZE + 1
      : LIST_PAGE_SIZE
    const params = searchGenerator(
      searchQuery,
      limit,
      offset,
      sort,
      undefined
    )

    api.get(
      url,
      { params }
    ).then(({ data }: { data: EncounterRecord[] }) => {
      const results: EncounterRecord[] = offset === 0
        ? data
        : concat(
          currentEncounters,
          data
        )
      const pages = offset === 0
        ? 1
        : pagesDisplayed + 1
      setEncounters(results)
      setWorklistView(view)
      setPagesDisplayed(pages)
      setEncounterSort(sort)
      setQueryFilters(query)
    })
  }

  useEffect(() => {
    const userFilters = currentUser.worklistFilters
    const sortValue = userFilters.encounterSort
      ? userFilters.encounterSort
      : 'start asc'
    const view = userFilters.view
      ? userFilters.view
      : WORKLIST_VIEW.CODER.WORKLIST
    const query: QueryFilters = {
      start: userFilters.start
        ? new Date(userFilters.start)
        : null,
      birthDate: userFilters.birthDate
        ? new Date(userFilters.birthDate)
        : null,
      firstName: userFilters.firstName
        ? userFilters.firstName
        : null,
      lastName: userFilters.lastName
        ? userFilters.lastName
        : null,
      patientOrgId: userFilters.patientOrgId
        ? userFilters.patientOrgId
        : null,
      appointmentType: userFilters.appointmentType
        ? userFilters.appointmentType
        : null,
      practitionerId: userFilters.practitionerId
        ? userFilters.practitionerId
        : null,
      patientRegion: userFilters.patientRegion
        ? userFilters.patientRegion
        : null,
      vbcProgram: userFilters.vbcProgram
        ? userFilters.vbcProgram
        : null
    }
    const hasFilter = userFilters.start ||
      query.birthDate ||
      query.firstName ||
      query.lastName ||
      query.patientOrgId ||
      query.appointmentType ||
      query.practitionerId ||
      query.patientRegion ||
      query.vbcProgram
    setIsFilterVisible(!!hasFilter)
    setEncounterSort(sortValue)
    fetchEncounters(
      view,
      0,
      query,
      sortValue,
      []
    )
  }, [])

  const onWorklistViewSelect = (
    event: React.SyntheticEvent,
    data: { value: string }
  ) => {
    const view = data.value
    const query = { ...queryFilters }
    const userFilters = currentUser.worklistFilters
    userFilters.view = view
    if (view !== WORKLIST_VIEW.CODER.WORKLIST) {
      const start = query.start && query.start instanceof Date
        ? query.start
        : null
      userFilters.start = start
      query.start = start
    }
    updateFilters(
      currentUser,
      userFilters
    )
    fetchEncounters(
      view,
      0,
      query,
      encounterSort,
      []
    )
  }

  const getFilterMetadata = (): any[][] => {
    const isVbcFilterVisible = showVbcDisplay(currentUser)
    const metadata: any[][] = [
      [
        {
          placeholder: 'MRN',
          name: 'mrn',
          width: 6,
          like: false
        },
        {
          placeholder: 'First Name',
          name: 'firstName',
          width: 5,
          like: true
        },
        {
          placeholder: 'Last Name',
          name: 'lastName',
          width: 5,
          like: true
        }
      ],
      [
        {
          placeholder: 'Appointment Date',
          name: 'start',
          width: 6,
          date: true
        },
        {
          placeholder: 'Date of Birth',
          name: 'birthDate',
          width: 5,
          date: true
        },
        {
          placeholder: 'Practice',
          name: 'patientOrgId',
          width: 5,
          organizationSelect: true
        }
      ],
      [
        {
          placeholder: 'Visit Type',
          name: 'appointmentType',
          width: 6,
          appointmentTypeSelect: true
        },
        {
          placeholder: 'Practitioner',
          name: 'practitionerId',
          width: 5,
          practitionerSelect: true
        },
        {
          placeholder: 'Region',
          name: 'patientRegion',
          width: 5,
          patientRegionSelect: true
        }
      ]
    ]

    if (isVbcFilterVisible) {
      metadata.push([
        {
          placeholder: 'VBC Program',
          name: 'vbcProgram',
          width: 6,
          vbcProgramSelect: true
        }
      ])
    }

    return metadata
  }

  const handleEncounterSort = (sort: string) => {
    const view = worklistView
    const query = queryFilters
    setEncounterSort(sort)
    fetchEncounters(
      view,
      0,
      query,
      sort,
      []
    )
    updateFilters(
      currentUser,
      { encounterSort: sort }
    )
  }

  const sortOnHeaderClick = (clickedColumn: string) => () => {
    let sort = encounterSort

    switch (clickedColumn) {
      case 'time':
        sort = sort === 'start asc'
          ? 'start desc'
          : 'start asc'
        handleEncounterSort(sort)
        break
      case 'appointmentType':
        sort = sort === 'appointmentType asc'
          ? 'appointmentType desc'
          : 'appointmentType asc'
        handleEncounterSort(sort)
        break
      case 'name':
        sort = sort === 'lastName asc'
          ? 'lastName desc'
          : 'lastName asc'
        handleEncounterSort(sort)
        break
      case 'dob':
        sort = sort === 'birthDate asc'
          ? 'birthDate desc'
          : 'birthDate asc'
        handleEncounterSort(sort)
        break
      case 'mrn':
        sort = sort === 'mrn asc'
          ? 'mrn desc'
          : 'mrn asc'
        handleEncounterSort(sort)
        break
      case 'vbcProgram':
        sort = sort === 'vbcProgram asc'
          ? 'vbcProgram desc'
          : 'vbcProgram asc'
        handleEncounterSort(sort)
        break
      case 'practitioner':
        sort = sort === 'practitionerLastName asc'
          ? 'practitionerLastName desc'
          : 'practitionerLastName asc'
        handleEncounterSort(sort)
        break
      case 'nameMobile':
        sort = sort === 'lastName asc'
          ? 'lastName desc'
          : 'lastName asc'
        handleEncounterSort(sort)
        break
      default:
        sort = 'start asc'
        handleEncounterSort(sort)
    }
  }

  const onMore = () => {
    const offset = LIST_PAGE_SIZE * pagesDisplayed + 1
    fetchEncounters(
      worklistView,
      offset,
      queryFilters,
      encounterSort,
      encounters
    )
  }

  const selectRecord = (
    id: string,
    event: React.MouseEvent
  ) => {
    if (onSelect && id) {
      onSelect(
        id,
        event
      )
    }
  }

  const toggleFilter = () => {
    setIsFilterVisible(!isFilterVisible)
  }

  const onSubmitFilter = (query: QueryFilters) => {
    updateFilters(
      currentUser,
      {
        start: query.start
          ? new Date(query.start)
          : null,
        birthDate: query.birthDate
          ? new Date(query.birthDate)
          : null,
        firstName: query.firstName
          ? query.firstName.replaceAll(
            '%',
            ''
          )
          : null,
        lastName: query.lastName
          ? query.lastName.replaceAll(
            '%',
            ''
          )
          : null,
        patientOrgId: query.patientOrgId
          ? query.patientOrgId
          : null,
        appointmentType: query.appointmentType
          ? query.appointmentType
          : null,
        practitionerId: query.practitionerId
          ? query.practitionerId
          : null,
        patientRegion: query.patientRegion
          ? query.patientRegion
          : null,
        vbcProgram: query.vbcProgram
          ? query.vbcProgram
          : null,
        view: worklistView
      }
    )
    fetchEncounters(
      worklistView,
      0,
      query,
      encounterSort,
      []
    )
  }

  const getFilterInitialValues = () => {
    const userFilters = currentUser.worklistFilters
    const initialValues: Record<string, string | null> = {}
    if (userFilters.start) {
      const start = format(
        new Date(userFilters.start),
        FORMAT.DATE
      )
      initialValues.start = start
    }
    if (userFilters.birthDate) {
      const birthDate = formatDob(
        new Date(userFilters.birthDate),
        FORMAT.DATE
      )
      initialValues.birthDate = birthDate
    }
    initialValues.firstName = userFilters.firstName
      ? userFilters.firstName
      : null
    initialValues.lastName = userFilters.lastName
      ? userFilters.lastName
      : null
    initialValues.patientOrgId = userFilters.patientOrgId
      ? userFilters.patientOrgId
      : null
    initialValues.appointmentType = userFilters.appointmentType
      ? userFilters.appointmentType
      : null
    initialValues.practitionerId = userFilters.practitionerId
      ? userFilters.practitionerId
      : null
    initialValues.patientRegion = userFilters.patientRegion
      ? userFilters.patientRegion
      : null
    initialValues.vbcProgram = userFilters.vbcProgram
      ? userFilters.vbcProgram
      : null
    return initialValues
  }

  const generateTableData = (records: EncounterRecord[]) => {
    if (!records || records.length === 0) return []

    const formatDate = (startDt: string) => format(
      new Date(startDt),
      FORMAT.DATE_TIME
    )

    return records.map(record => {
      const { patient } = record
      const { practitioner } = record
      const patientName = `${patient.firstName} ${patient.lastName}`
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const mrn = patientMrn(patient as any)
      const { vbcProgram } = patient
      const practitionerName = `${practitioner.firstName} ${practitioner.lastName}`
      const mobileName = `${patient.firstName} ${patient.lastName} ( ${formatDob(
        patient.birthDate,
        FORMAT.DATE
      )} )`

      return {
        id: record.id,
        time: formatDate(record.start),
        name: patientName,
        dob: formatDob(
          record.patient.birthDate,
          FORMAT.DATE
        ),
        mrn: semiColonsToLineBreaks(mrn || ''),
        vbcProgram,
        appointmentType: record.appointmentType,
        practitioner: practitionerName,
        nameMobile: mobileName,
        statusIcon: (
          <CoderEncounterStatusIcon
            encounter={ record as any }
            currentUser={ currentUser as any }
          />
        ),
        meta: { className: 'cursor-pointer' }
      }
    })
  }

  const getLeftSubHeader = () => {
    const prefix = 'Coder Worklist'
    switch (worklistView) {
      case WORKLIST_VIEW.CODER.READY_FOR_CODER_QA:
        return `${prefix} - Ready for Coder QA`
      case WORKLIST_VIEW.CODER.COMPLETED:
        return `${prefix} - Complete`
      case WORKLIST_VIEW.CODER.READY_FOR_PHYSICIAN:
        return `${prefix} - Ready for Physician`
      default:
        return prefix
    }
  }

  const getRightSubHeader = () => {
    const tooltip = isFilterVisible
      ? 'Hide Worklist Filters'
      : 'Show Worklist Filters'
    return (
      <Popup
        content={ tooltip }
        mouseEnterDelay={ 1000 }
        trigger={
          shouldRenderReskin2025
            ? (
              <ReveleerControlButton
                icon={ FilterIcon }
                id="worklist-filter"
                isDisabled={ false }
                onClick={ toggleFilter }
              />
            )
            : (
              <Icon
                id="worklist-filter"
                name="filter"
                size="large"
                className="pointer"
                onClick={ toggleFilter }
              />
            )
        }
      />
    )
  }

  const renderWorklistTable = () => {
    const tableData = generateTableData(encounters)
    const computerColumns = [
      {
        name: '',
        value: 'statusIcon'
      },
      {
        name: 'Time',
        value: 'time'
      },
      {
        name: 'Type',
        value: 'appointmentType'
      },
      {
        name: 'Name',
        value: 'name'
      },
      {
        name: 'DOB',
        value: 'dob'
      },
      {
        name: 'MRN(s)',
        value: 'mrn'
      },
      {
        name: 'Practitioner',
        value: 'practitioner'
      }
    ]

    if (showVbcDisplay(currentUser)) {
      computerColumns.splice(
        7,
        0,
        {
          name: 'VBC Program',
          value: 'vbcProgram'
        }
      )
    }

    let shouldDisplayMore = false
    let shouldDisplayMaxRecords = false

    if (tableData.length > pagesDisplayed * LIST_PAGE_SIZE) {
      shouldDisplayMore = pagesDisplayed < LIST_MAX_PAGES
      shouldDisplayMaxRecords = pagesDisplayed >= LIST_MAX_PAGES
      tableData.pop()
    }

    return (
      <span>
        <Table
          key={ worklistView }
          computerColumns={ computerColumns }
          mobileColumns={ [
            {
              value: 'time',
              width: '4'
            },
            {
              value: 'nameMobile',
              width: '12'
            }
          ] }
          data={ tableData }
          onClick={ selectRecord }
          more={ shouldDisplayMore }
          max={ shouldDisplayMaxRecords }
          onMore={ onMore }
          sortOnHeaderClick={ sortOnHeaderClick }
        />
      </span>
    )
  }

  return (
    <div className="header-padding">
      <Container>
        <SubHeader
          left={ getLeftSubHeader() }
          right={ getRightSubHeader() }
        />
        { isFilterVisible && React.createElement(
          Filter as any,
          {
            submit: onSubmitFilter,
            filters: getFilterMetadata(),
            initialValues: getFilterInitialValues()
          }
        ) }
        <SafeSegment
          attached
          className="no-margin"
        >
          <SafeGrid>
            <Grid.Row style={ {
              paddingBottom: '0rem',
              textAlign: 'right'
            } }>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <Form.Field
                  label="View: "
                  control={ Select }
                  value={ worklistView }
                  options={ worklistViewOptions }
                  onChange={ onWorklistViewSelect }
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                { renderWorklistTable() }
              </Grid.Column>
            </Grid.Row>
          </SafeGrid>
        </SafeSegment>
      </Container>
    </div>
  )
}

const ConnectedCoderWorklist = connect(
  (state: RootState) => ({
    currentUser: state.user
  }),
  { updateUserWorklistFilters }
)(CoderWorklist as any)

export default ConnectedCoderWorklist as React.ComponentType<CoderWorklistOwnProps>
